import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { Bot, X, Send, ChevronDown, Lightbulb, RotateCcw, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Whitelist ROLES_AUTORISES retirée au profit de la matrice
//   RBAC `ai_chat_utiliser` (canDo). Le commentaire TODO d'origine est archivé dans CATALOGUE_ACTIONS_PAR_PAGE_V13_61.md.
//   NB : Backend `/api/chat-ia/message` reste à guarder au niveau router (backlog P2, hors scope Vague 5b).

export function AIChat() {
  const { user } = useAuth();
  const { permissions, canDo } = usePermissions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const messagesEndRef = useRef(null);

  // 🔀 [Phase v13.61 Vague 5b — 2026-08-25] Branchement matrice RBAC via `ai_chat_utiliser`.
  //   Seed préserve : owner + charge_projet + adjoint_admin = True (comportement inchangé).
  const roleOk = canDo('ai_chat_utiliser');

  useEffect(() => {
    if (user?.id) {
      const sid = `chat_${user.id}_${Date.now()}`;
      setSessionId(sid);
    }
  }, [user?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!roleOk) return null;

  const sendMessage = async () => {
    const texte = input.trim();
    if (!texte || loading) return;

    const userMsg = { role: "user", texte, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const r = await axios.post(`${API_URL}/api/chat-ia/message`, {
          user_id: user?.id || "",
          texte,
          session_id: sessionId,
          user_role: user?.role || "",
          user_permissions: permissions || {},
        });
      if (r.status === 200) {
        const d = r.data;
        const botMsg = {
          role: "assistant",
          texte: d.reponse,
          propose_creation: d.propose_creation,
          data_connected: d.data_connected,
          permissions_denied: d.permissions_denied,
          id: Date.now() + 1,
        };
        setMessages(prev => [...prev, botMsg]);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          texte: "Désolé, une erreur s'est produite. Réessayez.",
          id: Date.now() + 1,
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        texte: "Service temporairement indisponible.",
        id: Date.now() + 1,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    await axios.delete(`${API_URL}/api/chat-ia/history/${sessionId}`);
    const newSid = `chat_${user?.id}_${Date.now()}`;
    setSessionId(newSid);
    setMessages([]);
  };

  const handleProposition = () => {
    setOpen(false);
    navigate("/propositions");
  };

  return (
    <>
      {/* Bouton flottant */}
      {!open && (
        <button
          data-testid="chat-ia-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 shadow-xl flex items-center justify-center hover:scale-105 transition-all duration-200 group"
          title="Assistant IA - Questions sur la plateforme"
        >
          <Bot className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white" />
        </button>
      )}

      {/* Fenêtre de chat */}
      {open && (
        <div
          data-testid="chat-ia-window"
          className="fixed bottom-24 right-6 z-[9999] w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ height: "480px" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">Assistant PRQC</p>
              <p className="text-blue-100 text-xs flex items-center gap-1">
                <Database className="w-3 h-3" /> Connecté aux données
              </p>
            </div>
            <button onClick={clearHistory} title="Effacer la conversation" className="text-blue-200 hover:text-white p-1">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} className="text-blue-200 hover:text-white p-1">
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-400 space-y-3 mt-4">
                <Bot className="w-12 h-12 mx-auto text-gray-200" />
                <p className="text-sm font-medium">Bonjour {user?.prenom} !</p>
                <p className="text-xs">Je suis connecté aux données de l'entreprise. Posez-moi vos questions !</p>
                <div className="space-y-1.5 text-left">
                  {[
                    "Combien de contrats ont été signés ce mois-ci?",
                    "Quel est le CA total YTD?",
                    "Combien d'employés actifs avons-nous?",
                    "Combien d'heures ont été travaillées ce mois?",
                  ].map(s => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); }}
                      className="w-full text-left px-3 py-2 rounded-lg bg-white border border-gray-200 text-xs text-gray-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] ${msg.role === "user" ? "" : ""}`}>
                  {msg.role === "assistant" && (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center mb-1">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div className={`px-3 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : "bg-white text-slate-800 rounded-bl-sm border border-gray-200 shadow-sm"
                  }`}>
                    {msg.texte}
                  </div>

                  {/* Indicateur données réelles */}
                  {msg.data_connected && msg.role === "assistant" && (
                    <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
                      <Database className="w-3 h-3" /> Réponse basée sur les données réelles
                    </p>
                  )}

                  {/* Suggestion de créer une proposition */}
                  {msg.propose_creation && msg.role === "assistant" && (
                    <button
                      onClick={handleProposition}
                      className="mt-2 w-full flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors"
                    >
                      <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span className="text-xs text-amber-700 font-medium">Créer une proposition officielle</span>
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <div className="flex gap-1.5 items-center">
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Zone de saisie */}
          <div className="px-3 py-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                data-testid="chat-ia-input"
                type="text"
                placeholder="Votre question..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendMessage()}
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
              <button
                data-testid="chat-ia-send"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
